import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Settings, ChevronDown, ChevronLeft, ChevronRight, Play, Pause, RotateCcw, UserCheck, Plus, X, CheckSquare, Lock, Flag, AlertTriangle, Folder, Check, Video } from 'lucide-react';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, startOfToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, subWeeks, addMonths, subMonths, parse, getDay, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import api from '../utils/api';
import CalendarColorSettingsModal from './CalendarColorSettingsModal';
import QuickAddTaskModal from './QuickAddTaskModal';

const localizer = dateFnsLocalizer({
  format,
  parse: (value) => new Date(value),
  startOfWeek: () => startOfToday(),
  getDay: (date) => date.getDay(),
  locales: { 'en-US': enUS }
});

// Custom event component for the calendar - defined outside to prevent re-creation
const CalendarEvent = React.memo(({ event, isTaskOverdue, onToggleComplete, completingBlockId }) => {
  const isOverdue = event.type === 'task' && isTaskOverdue(event.resource);
  const isWorkBlock = event.type === 'work-block';
  const blockId = event.resource?.blockId || event.resource?.id;
  const isLoading = completingBlockId === blockId;
  
  const handleCheckboxClick = (e) => {
    e.stopPropagation(); // Prevent opening the modal
    if (onToggleComplete && blockId) {
      onToggleComplete(blockId, false); // Always marking as complete (since completed blocks are filtered out)
    }
  };
  
  return (
    <div className="text-xs h-full">
      <div className="flex items-start gap-1">
        {/* Checkbox for work blocks */}
        {isWorkBlock && (
          <button
            onClick={handleCheckboxClick}
            disabled={isLoading}
            className={`flex-shrink-0 w-4 h-4 mt-0.5 rounded border-2 flex items-center justify-center transition-all
              border-white/70 hover:border-white hover:bg-white/20
              ${isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'}
            `}
            title="Mark complete"
          >
            {isLoading && (
              <div className="w-2 h-2 border border-white/70 border-t-transparent rounded-full animate-spin" />
            )}
          </button>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">
            {event.title}
          </div>
          {(event.resource?.segmentNumber || event.resource?.block_number) && (
            <div className="text-xs opacity-75">
              Part {event.resource.segmentNumber || event.resource.block_number}/{event.resource.totalSegments || event.resource.total_blocks}
            </div>
          )}
          {isOverdue && (
            <div className="text-xs font-bold opacity-100 flash">
              OVERDUE
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

const ScheduleDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [workBlocks, setWorkBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoScheduling, setAutoScheduling] = useState(true);
  const [selectedUser, setSelectedUser] = useState('me');
  const [teamMembers, setTeamMembers] = useState([]);
  const [completingBlock, setCompletingBlock] = useState(null);
  const [workHours, setWorkHours] = useState({
    start: '09:00',
    end: '17:00',
    work_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    daily_schedule: null,
    timezone: 'America/New_York',
    background: '#F9FAFB'
  });
  
  // Fetch user's work hours on mount
  useEffect(() => {
    const fetchWorkHours = async () => {
      try {
        const response = await api.get('/users/work-hours');
        
        if (response.data) {
          setWorkHours(prev => ({
            ...prev,
            start: response.data.work_hours_start.substring(0, 5), // HH:MM format
            end: response.data.work_hours_end.substring(0, 5),
            work_days: response.data.work_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            daily_schedule: response.data.daily_schedule || null,
            timezone: response.data.timezone
          }));
        }
      } catch (error) {
        console.error('Failed to fetch work hours, using defaults:', error);
      }
    };
    
    fetchWorkHours();
  }, []);
  const [currentView, setCurrentView] = useState('week');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [lastRescheduleTime, setLastRescheduleTime] = useState(Date.now());
  const [showQuickAddTask, setShowQuickAddTask] = useState(false);
  const [showColorSettings, setShowColorSettings] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  
  // Schedule view mode: 'my' (personal), 'team-day' (all team for one day), 'team-user' (one user's timeframe)
  const [scheduleView, setScheduleView] = useState('my');
  const [teamViewTimeframe, setTeamViewTimeframe] = useState('week'); // 'week', '2week', 'month'
  const [teamViewUser, setTeamViewUser] = useState(null);
  const [teamDaySchedule, setTeamDaySchedule] = useState(null);
  const [teamUserSchedule, setTeamUserSchedule] = useState(null);
  const [teamViewDate, setTeamViewDate] = useState(new Date());
  const [teamViewLoading, setTeamViewLoading] = useState(false);
  
  // Smart schedule refresh - gap detection and inactivity-based refresh
  const [hasScheduleGaps, setHasScheduleGaps] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const [isOptimizing, setIsOptimizing] = useState(false);
  const INACTIVITY_THRESHOLD = 4 * 60 * 1000; // 4 minutes
  const MIN_GAP_MINUTES = 15; // Ignore gaps of 15 minutes or less
  
  const [userColorSettings, setUserColorSettings] = useState({
    task_critical: '#DC2626',
    task_high: '#D97706', 
    task_normal: '#6B46C1',
    task_low: '#059669',
    task_overdue: '#B91C1C',
    meeting: '#059669',
    background: '#F9FAFB'
  });
  
  // Import permissions hook
  const { hasPermission, isAdmin: permissionIsAdmin, canViewAllTasks } = require('../hooks/usePermissions').usePermissions();
  
  // User role - kept for backward compatibility but primarily use permissions hook
  const [userRole, setUserRole] = useState(null);
  // Rely on permissions system - permissionIsAdmin uses 'team.manage_settings' permission
  const isAdmin = permissionIsAdmin;
  
  // Sub-teams (displayed as "Teams" in UI) for filtering team views
  const [subTeams, setSubTeams] = useState([]);
  const [selectedSubTeam, setSelectedSubTeam] = useState(null); // null = show all
  const [canManageSubTeams, setCanManageSubTeams] = useState(false);
  const [userSubTeamIds, setUserSubTeamIds] = useState([]);
  const [subTeamsEnabled, setSubTeamsEnabled] = useState(false); // Feature toggle

  useEffect(() => {
    fetchInitialData();
    
    // Set up real-time rescheduling check every 5 minutes
    const rescheduleInterval = setInterval(() => {
      if (autoScheduling && selectedUser === 'me') {
        checkAndRescheduleOverdueTasks();
      }
    }, 5 * 60 * 1000); // Every 5 minutes
    
    // Listen for color settings updates
    const handleColorUpdate = (event) => {
      setUserColorSettings(event.detail);
    };
    
    window.addEventListener('calendarColorsUpdated', handleColorUpdate);
    
    return () => {
      clearInterval(rescheduleInterval);
      window.removeEventListener('calendarColorsUpdated', handleColorUpdate);
    };
  }, []);

  // Check for overdue work blocks and trigger backend reschedule
  const checkAndRescheduleOverdueTasks = useCallback(async () => {
    try {
      const response = await api.post('/schedule/reschedule-overdue');
      if (response.data?.rescheduled > 0) {
        console.log(`Rescheduled ${response.data.rescheduled} overdue blocks`);
        // Refresh work blocks
        fetchUserData(selectedUser);
      }
    } catch (error) {
      console.error('Failed to reschedule overdue blocks:', error);
    }
  }, [selectedUser]);

  // Helper function to determine if viewing another person's schedule
  const isViewingOtherSchedule = () => {
    return selectedUser !== 'me' && selectedUser !== user?.id;
  };

  // Helper function to process events for privacy
  const processEventsForPrivacy = (eventsList) => {
    if (!isViewingOtherSchedule()) {
      return eventsList;
    }

    return eventsList.map(event => {
      // If this is a personal task and we're viewing someone else's schedule
      if (event.type === 'task' && event.resource?.is_personal) {
        return {
          ...event,
          title: 'Personal Task',
          resource: {
            ...event.resource,
            title: 'Personal Task',
            name: 'Personal Task',
            description: 'Private personal task',
            privacy_masked: true
          }
        };
      }
      return event;
    });
  };

  // Event click handler
  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  // Edit task handler - navigate to task URL for consistent behavior
  const handleEditTask = async () => {
    if (selectedEvent && selectedEvent.type === 'task') {
      const taskId = selectedEvent.resource.id;
      console.log('🔗 Navigating to task:', taskId);
      setShowEventModal(false);
      navigate(`/tasks/${taskId}`);
    }
  };

  // Task refresh handler (called when returning from task modal via event)
  useEffect(() => {
    const handleTaskUpdated = () => {
      console.log('📅 Refreshing schedule after task update');
      fetchUserData(selectedUser);
    };
    
    window.addEventListener('taskUpdated', handleTaskUpdated);
    return () => window.removeEventListener('taskUpdated', handleTaskUpdated);
  }, [selectedUser]);

  const handleHideEvent = async () => {
    if (!selectedEvent || !selectedEvent.resource?.id) return;
    
    if (window.confirm('Hide this event from your schedule? It will remain in Google Calendar but won\'t block your task availability.')) {
      try {
        await api.put(`/meetings/${selectedEvent.resource.id}/hide-from-schedule`, {});
        
        // Close modal and refresh
        setShowEventModal(false);
        setSelectedEvent(null);
        
        // Refresh the calendar data
        await fetchUserData(selectedUser);
        
      } catch (error) {
        console.error('Failed to hide event:', error);
        alert(`Failed to hide event: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Fetch user info and role
      try {
        const userResponse = await api.get('/users/profile');
        
        // Fetch user work hours if available
        if (userResponse.data.work_hours) {
          setWorkHours(userResponse.data.work_hours);
        }
      } catch (error) {
        console.error('Failed to fetch user profile:', error.message);
      }
      
      // Fetch team info to determine role
      try {
        const teamResponse = await api.get('/team');
        
        const fetchedUserRole = teamResponse.data.membership?.role;
        setUserRole(fetchedUserRole);
        // isAdmin is now computed from usePermissions hook
        setTeamMembers(teamResponse.data.members || []);
      } catch (error) {
        console.error('Failed to fetch team info:', error.message);
        // Continue without team info
      }
      
      // Fetch sub-teams for filtering team views
      try {
        const subTeamsResponse = await api.get('/sub-teams');
        const isEnabled = subTeamsResponse.data.enabled || false;
        setSubTeamsEnabled(isEnabled);
        
        if (isEnabled) {
          const fetchedSubTeams = subTeamsResponse.data.sub_teams || [];
          setSubTeams(fetchedSubTeams);
          setCanManageSubTeams(subTeamsResponse.data.can_manage || false);
          setUserSubTeamIds(subTeamsResponse.data.user_sub_team_ids || []);
          
          // Set default sub-team filter to user's first sub-team (alphabetically)
          if (subTeamsResponse.data.user_sub_team_ids?.length > 0) {
            const userTeams = fetchedSubTeams.filter(st => 
              subTeamsResponse.data.user_sub_team_ids.includes(st.id)
            ).sort((a, b) => a.name.localeCompare(b.name));
            if (userTeams.length > 0) {
              setSelectedSubTeam(userTeams[0].id);
            }
          }
        } else {
          // Feature not enabled - reset state
          setSubTeams([]);
          setCanManageSubTeams(subTeamsResponse.data.can_manage || false);
          setUserSubTeamIds([]);
          setSelectedSubTeam(null);
        }
      } catch (error) {
        console.error('Failed to fetch sub-teams:', error.message);
      }
      
      // Fetch user color settings
      try {
        const colorResponse = await api.get('/users/calendar-colors');
        if (colorResponse.data) {
          setUserColorSettings({...userColorSettings, ...colorResponse.data});
        }
      } catch (error) {
        // Use default color settings (no error log needed)
      }
      
      // Fetch tasks and meetings (this will trigger auto-scheduling via useEffect)
      await fetchUserData(selectedUser);
      
    } catch (error) {
      console.error('Error in fetchInitialData:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (userId) => {
    try {
      const isCurrentUser = userId === 'me';
      
      // Fetch tasks with due dates for calendar
      let tasksData = [];
      try {
        const tasksResponse = await api.get('/schedule/tasks');
        tasksData = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];
        setTasks(tasksData);
      } catch (taskError) {
        console.error('Failed to fetch tasks:', taskError.message);
        setTasks([]);
      }
      
      // Auto-generate work blocks for tasks that don't have them (only for current user)
      if (isCurrentUser && tasksData.length > 0) {
        try {
          const generateResponse = await api.post('/schedule/auto-generate-blocks');
          if (generateResponse.data?.generated > 0) {
            console.log(`Auto-generated work blocks for ${generateResponse.data.generated} tasks`);
          }
        } catch (genError) {
          console.error('Failed to auto-generate work blocks:', genError.message);
        }
      }
      
      // Fetch work blocks from backend scheduler
      try {
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30);
        
        const blocksResponse = await api.get('/schedule/work-blocks', {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString()
          }
        });
        
        const blocksData = Array.isArray(blocksResponse.data) ? blocksResponse.data : [];
        setWorkBlocks(blocksData);
      } catch (blockError) {
        console.error('Failed to fetch work blocks:', blockError.message);
        setWorkBlocks([]);
      }
      
      // Fetch meetings from meetings table
      try {
        const meetingsResponse = await api.get('/meetings');
        
        const meetingsData = meetingsResponse.data?.meetings || [];
        setMeetings(meetingsData);
      } catch (meetingError) {
        console.error('Failed to fetch meetings:', meetingError.message);
        setMeetings([]);
      }
      
    } catch (error) {
      console.error('Error in fetchUserData:', error.message);
      setTasks([]);
      setMeetings([]);
      setWorkBlocks([]);
    }
  };

  // Legacy function - no longer used, meetings now fetched from /api/meetings
  // const fetchMeetingsFromIntegrations = async (userId, token) => { ... }

  // ============================================================================
  // AUTO-SCHEDULING ENGINE
  // ============================================================================
  // All date/time operations use the user's LOCAL TIMEZONE:
  // - Work hours (e.g., "9:00 AM") are interpreted in local time
  // - "Now" comparisons use local time
  // - Due dates are parsed as local dates (YYYY-MM-DD format)
  // - Calendar displays times in local timezone
  // ============================================================================
  
  // Helper function to convert UTC time to user's timezone
  const convertToUserTimezone = useCallback((utcDateString) => {
    const userTimezone = workHours.timezone || 'America/New_York';
    const utcDate = new Date(utcDateString);
    return toZonedTime(utcDate, userTimezone);
  }, [workHours.timezone]);
  
  const scheduleTasksAutomatically = useCallback(() => {
    // Convert meetings to calendar event format
    // Meetings are stored in UTC, convert to user's timezone for display
    const meetingEvents = meetings.map(meeting => ({
      id: `meeting-${meeting.id}`,
      title: meeting.title || 'Meeting',
      start: convertToUserTimezone(meeting.start_time),
      end: convertToUserTimezone(meeting.end_time),
      type: 'meeting',
      resource: meeting
    }));
    
    // Convert work blocks to calendar events
    // Filter out completed blocks - they shouldn't show on the calendar
    // Work blocks are stored in UTC, convert to user's timezone for display
    const workBlockEvents = workBlocks
      .filter(block => block.status !== 'completed')
      .map(block => {
        const blockTitle = block.total_blocks > 1 
          ? `${block.task_name} (${block.block_number}/${block.total_blocks})`
          : block.task_name;
        
        return {
          id: `work-block-${block.id}`,
          title: blockTitle,
          start: convertToUserTimezone(block.scheduled_start),
          end: convertToUserTimezone(block.scheduled_end),
          type: 'work-block',
          resource: {
            ...block,
            // Explicitly set these for the modal
            task_id: block.task_id,
            due_date: block.due_date,
            isCompleted: false,
            blockId: block.id,
            segmentNumber: block.block_number,
            totalSegments: block.total_blocks
          }
        };
      });
    
    // Combine meetings and work blocks - all tasks are now backend-managed
    setEvents(processEventsForPrivacy([...meetingEvents, ...workBlockEvents]));
  }, [meetings, workBlocks, convertToUserTimezone]);

  // Call scheduling function whenever data changes
  // This automatically triggers when meetings/workBlocks are loaded or updated
  useEffect(() => {
    if (meetings.length > 0 || workBlocks.length > 0) {
      scheduleTasksAutomatically();
    }
  }, [meetings, workBlocks, scheduleTasksAutomatically]);

  const handleUserChange = async (userId) => {
    setSelectedUser(userId);
    setLoading(true);
    await fetchUserData(userId);
    setLoading(false);
  };

  const toggleAutoScheduling = () => {
    setAutoScheduling(!autoScheduling);
    if (!autoScheduling) {
      scheduleTasksAutomatically();
    }
  };

  const rescheduleAll = async () => {
    console.log('🔄 Reschedule All clicked - regenerating all work blocks...');
    setLoading(true);
    try {
      // Call backend to clear and regenerate all blocks
      const response = await api.post('/schedule/regenerate-all-blocks');
      console.log('✅ Regeneration completed:', response.data);
      
      // Refresh work blocks from backend
      await fetchUserData(selectedUser);
      
      // Reset gap detection after reschedule
      setHasScheduleGaps(false);
      setLastActivityTime(Date.now());
      
      alert(`Rescheduled ${response.data?.regenerated || 0} tasks successfully!`);
    } catch (error) {
      console.error('❌ Error during reschedule:', error);
      alert('Error rescheduling tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ============ Smart Schedule Refresh - Gap Detection ============
  
  // Detect gaps in today's schedule (gaps > 15 minutes during work hours)
  const detectScheduleGaps = useCallback(() => {
    if (scheduleView !== 'my') return false;
    
    const today = new Date();
    const todayStr = format(today, 'yyyy-MM-dd');
    
    // Get work hours for today
    const dayName = format(today, 'EEEE').toLowerCase();
    if (!workHours.work_days?.includes(dayName)) return false; // Not a work day
    
    // Parse work hours
    const [startHour, startMin] = workHours.start.split(':').map(Number);
    const [endHour, endMin] = workHours.end.split(':').map(Number);
    
    const workStart = new Date(today);
    workStart.setHours(startHour, startMin, 0, 0);
    
    const workEnd = new Date(today);
    workEnd.setHours(endHour, endMin, 0, 0);
    
    const now = new Date();
    
    // Start from now if we're past work start, otherwise from work start
    const scheduleStart = now > workStart ? now : workStart;
    
    // If we're past work end, no gaps to detect
    if (now >= workEnd) return false;
    
    // Get today's work blocks and meetings, sorted by start time
    const todayEvents = events
      .filter(e => {
        const eventDate = format(new Date(e.start), 'yyyy-MM-dd');
        return eventDate === todayStr && new Date(e.end) > scheduleStart;
      })
      .sort((a, b) => new Date(a.start) - new Date(b.start));
    
    // Check for gaps
    let currentTime = scheduleStart;
    
    for (const event of todayEvents) {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);
      
      // Skip events that have already ended
      if (eventEnd <= currentTime) continue;
      
      // If event starts after current time, check gap size
      if (eventStart > currentTime) {
        const gapMinutes = (eventStart - currentTime) / (1000 * 60);
        
        if (gapMinutes > MIN_GAP_MINUTES) {
          console.log(`📊 Gap detected: ${Math.round(gapMinutes)} minutes from ${format(currentTime, 'h:mm a')} to ${format(eventStart, 'h:mm a')}`);
          return true;
        }
      }
      
      // Move current time to end of this event
      if (eventEnd > currentTime) {
        currentTime = eventEnd;
      }
    }
    
    // Check for gap at end of day (between last event and work end)
    if (currentTime < workEnd) {
      const gapMinutes = (workEnd - currentTime) / (1000 * 60);
      if (gapMinutes > MIN_GAP_MINUTES) {
        console.log(`📊 End-of-day gap detected: ${Math.round(gapMinutes)} minutes until ${format(workEnd, 'h:mm a')}`);
        return true;
      }
    }
    
    return false;
  }, [events, workHours, scheduleView, MIN_GAP_MINUTES]);

  // Check for gaps whenever events change
  useEffect(() => {
    if (scheduleView === 'my' && events.length > 0) {
      const hasGaps = detectScheduleGaps();
      setHasScheduleGaps(hasGaps);
    }
  }, [events, detectScheduleGaps, scheduleView]);

  // Track user activity
  useEffect(() => {
    const handleActivity = () => {
      setLastActivityTime(Date.now());
    };
    
    // Track mouse movement, clicks, and keyboard
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('click', handleActivity);
    window.addEventListener('keydown', handleActivity);
    
    return () => {
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, []);

  // Auto-refresh after inactivity if gaps exist
  useEffect(() => {
    if (!hasScheduleGaps || scheduleView !== 'my') return;
    
    const checkInactivity = setInterval(async () => {
      const inactiveTime = Date.now() - lastActivityTime;
      
      if (inactiveTime >= INACTIVITY_THRESHOLD && hasScheduleGaps && !isOptimizing) {
        console.log('⏰ Inactivity threshold reached with gaps - auto-optimizing schedule');
        setIsOptimizing(true);
        
        try {
          await api.post('/schedule/regenerate-all-blocks');
          await fetchUserData(selectedUser);
          setHasScheduleGaps(false);
          console.log('✅ Schedule auto-optimized after inactivity');
        } catch (error) {
          console.error('Auto-optimize failed:', error);
        } finally {
          setIsOptimizing(false);
          setLastActivityTime(Date.now());
        }
      }
    }, 30000); // Check every 30 seconds
    
    return () => clearInterval(checkInactivity);
  }, [hasScheduleGaps, lastActivityTime, scheduleView, selectedUser, isOptimizing, INACTIVITY_THRESHOLD]);

  // Manual optimize handler
  const handleOptimizeSchedule = async () => {
    setIsOptimizing(true);
    try {
      await api.post('/schedule/regenerate-all-blocks');
      await fetchUserData(selectedUser);
      setHasScheduleGaps(false);
      setLastActivityTime(Date.now());
    } catch (error) {
      console.error('Optimize failed:', error);
      alert('Failed to optimize schedule. Please try again.');
    } finally {
      setIsOptimizing(false);
    }
  };

  // ============ Team Schedule Functions ============
  
  // Check if user can view team schedules (leadership roles)
  const canViewTeamSchedules = ['owner', 'admin', 'project_manager'].includes(user?.role);

  // Get date range for team user view based on timeframe
  const getTeamDateRange = useCallback(() => {
    switch (teamViewTimeframe) {
      case '2week':
        const twoWeekStart = startOfWeek(teamViewDate, { weekStartsOn: 1 });
        return { start: twoWeekStart, end: addDays(twoWeekStart, 14) };
      case 'month':
        return { start: startOfMonth(teamViewDate), end: endOfMonth(teamViewDate) };
      case 'week':
      default:
        return { 
          start: startOfWeek(teamViewDate, { weekStartsOn: 1 }), 
          end: endOfWeek(teamViewDate, { weekStartsOn: 1 }) 
        };
    }
  }, [teamViewDate, teamViewTimeframe]);

  // Fetch team day schedule (all team members for one day)
  const fetchTeamDaySchedule = useCallback(async () => {
    if (scheduleView !== 'team-day') return;
    
    setTeamViewLoading(true);
    try {
      const dateStr = format(teamViewDate, 'yyyy-MM-dd');
      const params = { date: dateStr };
      if (selectedSubTeam) {
        params.sub_team_id = selectedSubTeam;
      }
      const response = await api.get(`/schedule/team/day`, { params });
      setTeamDaySchedule(response.data);
    } catch (err) {
      console.error('Error fetching team day schedule:', err);
    } finally {
      setTeamViewLoading(false);
    }
  }, [scheduleView, teamViewDate, selectedSubTeam]);

  // Fetch team user schedule (one user for extended date range to match personal schedule)
  const fetchTeamUserSchedule = useCallback(async () => {
    if (scheduleView !== 'team-user' || !teamViewUser) return;
    
    setTeamViewLoading(true);
    try {
      // Use a wide date range to match the personal schedule (7 days ago to 30 days ahead)
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 60); // 60 days ahead to cover month view
      
      const response = await api.get(`/schedule/team/user/${teamViewUser.id}`, {
        params: {
          start: start.toISOString(),
          end: end.toISOString()
        }
      });
      setTeamUserSchedule(response.data);
    } catch (err) {
      console.error('Error fetching team user schedule:', err);
    } finally {
      setTeamViewLoading(false);
    }
  }, [scheduleView, teamViewUser]);

  // Effect to fetch team schedules when view changes
  useEffect(() => {
    if (scheduleView === 'team-day') {
      fetchTeamDaySchedule();
    } else if (scheduleView === 'team-user') {
      fetchTeamUserSchedule();
    }
  }, [scheduleView, fetchTeamDaySchedule, fetchTeamUserSchedule]);

  // Set default team view user when switching to team-user view
  useEffect(() => {
    if (scheduleView === 'team-user' && !teamViewUser && teamMembers.length > 0) {
      const firstMember = teamMembers[0];
      setTeamViewUser(firstMember.user || firstMember);
    }
  }, [scheduleView, teamViewUser, teamMembers]);

  // Convert team day schedule to BigCalendar events
  const teamDayEvents = useMemo(() => {
    if (!teamDaySchedule?.team_members) return [];
    
    const events = [];
    teamDaySchedule.team_members.forEach(member => {
      // Work blocks
      (member.schedule?.work_blocks || []).forEach(block => {
        if (block.scheduled_start && block.scheduled_end) {
          events.push({
            id: `block-${block.id}`,
            title: `${member.user.name}: ${block.task_name}`,
            start: new Date(block.scheduled_start),
            end: new Date(block.scheduled_end),
            type: 'work-block',
            resource: {
              ...block,
              userName: member.user.name,
              userId: member.user.id
            }
          });
        }
      });
      
      // Meetings
      (member.schedule?.meetings || []).forEach(meeting => {
        if (meeting.start_time && meeting.end_time) {
          events.push({
            id: `meeting-${meeting.id}`,
            title: `${member.user.name}: ${meeting.title}`,
            start: new Date(meeting.start_time),
            end: new Date(meeting.end_time),
            type: 'meeting',
            resource: {
              ...meeting,
              userName: member.user.name,
              userId: member.user.id
            }
          });
        }
      });
    });
    
    return events;
  }, [teamDaySchedule]);

  // Convert team user schedule to BigCalendar events
  const teamUserEvents = useMemo(() => {
    if (!teamUserSchedule) return [];
    
    const events = [];
    const userName = teamUserSchedule.user?.name || teamViewUser?.name || '';
    
    // Work blocks
    (teamUserSchedule.work_blocks || []).forEach(block => {
      if (block.scheduled_start && block.scheduled_end) {
        events.push({
          id: `block-${block.id}`,
          title: block.task_name,
          start: new Date(block.scheduled_start),
          end: new Date(block.scheduled_end),
          type: 'work-block',
          resource: {
            ...block,
            userName
          }
        });
      }
    });
    
    // Meetings
    (teamUserSchedule.meetings || []).forEach(meeting => {
      if (meeting.start_time && meeting.end_time) {
        events.push({
          id: `meeting-${meeting.id}`,
          title: meeting.title,
          start: new Date(meeting.start_time),
          end: new Date(meeting.end_time),
          type: 'meeting',
          resource: {
            ...meeting,
            userName
          }
        });
      }
    });
    
    return events;
  }, [teamUserSchedule, teamViewUser]);

  // Team view navigation handlers
  const goToPreviousDay = () => setTeamViewDate(d => addDays(d, -1));
  const goToNextDay = () => setTeamViewDate(d => addDays(d, 1));
  const goToPreviousPeriod = () => {
    if (teamViewTimeframe === 'month') setTeamViewDate(d => subMonths(d, 1));
    else if (teamViewTimeframe === '2week') setTeamViewDate(d => subWeeks(d, 2));
    else setTeamViewDate(d => subWeeks(d, 1));
  };
  const goToNextPeriod = () => {
    if (teamViewTimeframe === 'month') setTeamViewDate(d => addMonths(d, 1));
    else if (teamViewTimeframe === '2week') setTeamViewDate(d => addWeeks(d, 2));
    else setTeamViewDate(d => addWeeks(d, 1));
  };
  const goToToday = () => setTeamViewDate(new Date());

  // Get period label for team user view
  const getTeamPeriodLabel = () => {
    const { start, end } = getTeamDateRange();
    if (teamViewTimeframe === 'month') return format(teamViewDate, 'MMMM yyyy');
    return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
  };

  // Format time for team schedule display
  const formatTeamTime = (isoString) => {
    if (!isoString) return '';
    const date = parseISO(isoString);
    const userTimezone = workHours.timezone || 'America/New_York';
    const zonedDate = toZonedTime(date, userTimezone);
    return format(zonedDate, 'h:mm a');
  };

  // Get priority color for team schedule items
  const getTeamPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'critical': return 'bg-red-100 border-red-400 text-red-800';
      case 'high': return 'bg-orange-100 border-orange-400 text-orange-800';
      case 'normal': return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'low': return 'bg-gray-100 border-gray-400 text-gray-800';
      default: return 'bg-purple-100 border-purple-400 text-purple-800';
    }
  };

  // ============ End Team Schedule Functions ============

  // Fetch team members filtered by sub-team when selection changes
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);
  
  useEffect(() => {
    const fetchFilteredMembers = async () => {
      if (scheduleView !== 'team-user') return;
      
      try {
        const params = selectedSubTeam ? { sub_team_id: selectedSubTeam } : {};
        const response = await api.get('/team/members-by-sub-team', { params });
        setFilteredTeamMembers(response.data || []);
        
        // If the currently selected user is not in the filtered list, clear selection
        if (teamViewUser && response.data?.length > 0) {
          const userStillInList = response.data.some(m => 
            (m.user?.id || m.id) === teamViewUser.id
          );
          if (!userStillInList) {
            const firstMember = response.data[0];
            setTeamViewUser(firstMember.user || firstMember);
          }
        } else if (response.data?.length > 0 && !teamViewUser) {
          const firstMember = response.data[0];
          setTeamViewUser(firstMember.user || firstMember);
        }
      } catch (error) {
        console.error('Failed to fetch filtered team members:', error);
        setFilteredTeamMembers(teamMembers);
      }
    };
    
    fetchFilteredMembers();
  }, [scheduleView, selectedSubTeam, teamMembers]);

  // Use filtered members for Team User view, regular members for Team Day (API handles filtering)
  const displayTeamMembers = scheduleView === 'team-user' ? 
    (filteredTeamMembers.length > 0 ? filteredTeamMembers : teamMembers) : 
    teamMembers;

  const handleTaskCreated = (newTask) => {
    // Refresh tasks after creating a new one
    fetchUserData(selectedUser);
  };

  const handleOpenFullEditor = (formData) => {
    // Handle opening full task editor with pre-filled data
    console.log('Opening full editor with data:', formData);
    // You can implement this to open the TaskModal with pre-filled data
  };

  // Helper function to check if a task is overdue based on its due date
  const isTaskOverdue = (task) => {
    // Use the backend-calculated is_overdue boolean
    // This is the single source of truth for overdue status
    return task?.is_overdue === true;
  };

  const eventStyleGetter = (event) => {
    const now = new Date();
    let backgroundColor = userColorSettings.task_normal; // Default to normal task color
    let opacity = 0.8;
    let border = '0px';
    let textDecoration = 'none';
    
    // Handle privacy-masked personal tasks
    if (event.resource?.privacy_masked) {
      return {
        style: {
          backgroundColor: '#9CA3AF', // Gray color for privacy
          opacity: 0.7,
          border: '1px solid #6B7280',
          color: '#374151',
          fontSize: '12px',
          fontStyle: 'italic'
        }
      };
    }
    
    // Handle work blocks
    if (event.type === 'work-block') {
      // Check if the work block's task is overdue
      const taskDueDate = event.resource?.due_date;
      let isOverdue = false;
      
      if (taskDueDate) {
        const now = new Date();
        // Parse the date correctly - due_date comes as "YYYY-MM-DD" or ISO string
        // Extract just the date part and create date in local timezone
        const datePart = taskDueDate.split('T')[0]; // Get "YYYY-MM-DD"
        const [year, month, day] = datePart.split('-').map(Number);
        // Create date at end of day in LOCAL timezone
        const dueDate = new Date(year, month - 1, day, 23, 59, 59, 999);
        isOverdue = dueDate < now;
      }
      
      if (isOverdue) {
        // Overdue work blocks: red color
        return {
          style: {
            backgroundColor: userColorSettings.task_overdue,
            borderRadius: '4px',
            opacity: 0.9,
            color: 'white',
            border: `2px solid ${userColorSettings.task_overdue}`,
            display: 'block'
          }
        };
      }
      
      // Active work blocks - color by priority
      const priorityColors = {
        'Critical': userColorSettings.task_critical,
        'High': userColorSettings.task_high,
        'Normal': userColorSettings.task_normal,
        'Low': userColorSettings.task_low
      };
      backgroundColor = priorityColors[event.resource?.priority] || userColorSettings.task_normal;
      border = '2px solid rgba(255,255,255,0.3)';
    } else if (event.type === 'meeting') {
      backgroundColor = userColorSettings.meeting;
    } else if (event.type === 'task') {
      // Check if task is overdue based on due date
      const isOverdue = isTaskOverdue(event.resource);
      
      if (isOverdue) {
        backgroundColor = userColorSettings.task_overdue;
        opacity = 0.9;
        border = `2px solid ${userColorSettings.task_overdue}`;
      } else {
        // Color based on priority using user settings
        const priorityColors = {
          'Critical': userColorSettings.task_critical,
          'High': userColorSettings.task_high,
          'Normal': userColorSettings.task_normal,
          'Low': userColorSettings.task_low
        };
        backgroundColor = priorityColors[event.resource?.priority] || userColorSettings.task_normal;
      }
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity,
        color: 'white',
        border,
        display: 'block',
        textDecoration
      }
    };
  };

  // Team event style getter - for team day and team user views
  const teamEventStyleGetter = (event) => {
    let backgroundColor = userColorSettings.task_normal;
    let border = '2px solid rgba(255,255,255,0.3)';
    
    if (event.type === 'work-block') {
      const priorityColors = {
        'Critical': userColorSettings.task_critical,
        'High': userColorSettings.task_high,
        'Normal': userColorSettings.task_normal,
        'Low': userColorSettings.task_low
      };
      backgroundColor = priorityColors[event.resource?.priority] || userColorSettings.task_normal;
    } else if (event.type === 'meeting') {
      backgroundColor = userColorSettings.meeting;
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.9,
        color: 'white',
        border,
        display: 'block',
        fontSize: '12px'
      }
    };
  };

  // Handle marking a work block as complete
  const handleCompleteBlock = async (blockId) => {
    try {
      setCompletingBlock(blockId);
      const response = await api.put(`/work-blocks/${blockId}/complete`);
      
      if (response.data.success) {
        // Update local state
        setWorkBlocks(prev => prev.map(block => 
          block.id === blockId 
            ? { ...block, status: 'completed', completed_at: new Date().toISOString() }
            : block
        ));
        
        // Update task percent complete in local tasks
        if (response.data.task_id) {
          setTasks(prev => prev.map(task =>
            task.id === response.data.task_id
              ? { ...task, percent_complete: response.data.percent_complete }
              : task
          ));
        }
        
        setShowEventModal(false);
      }
    } catch (error) {
      console.error('Failed to complete block:', error);
      alert('Failed to mark block as complete. Please try again.');
    } finally {
      setCompletingBlock(null);
    }
  };

  // Handle marking a work block as incomplete (undo)
  const handleUncompleteBlock = async (blockId) => {
    try {
      setCompletingBlock(blockId);
      const response = await api.put(`/work-blocks/${blockId}/incomplete`);
      
      if (response.data.success) {
        // Update local state
        setWorkBlocks(prev => prev.map(block => 
          block.id === blockId 
            ? { ...block, status: 'scheduled', completed_at: null }
            : block
        ));
        
        // Update task percent complete
        if (response.data.task_id) {
          setTasks(prev => prev.map(task =>
            task.id === response.data.task_id
              ? { ...task, percent_complete: response.data.percent_complete }
              : task
          ));
        }
        
        setShowEventModal(false);
      }
    } catch (error) {
      console.error('Failed to uncomplete block:', error);
      alert('Failed to undo completion. Please try again.');
    } finally {
      setCompletingBlock(null);
    }
  };

  // Generate work blocks for a task
  const [generatingBlocks, setGeneratingBlocks] = useState(false);
  
  const handleGenerateBlocks = async (taskId, forceRegenerate = false) => {
    try {
      setGeneratingBlocks(true);
      const response = await api.post(`/tasks/${taskId}/generate-blocks`, {
        force_regenerate: forceRegenerate
      });
      
      if (response.data.success) {
        // Refresh work blocks
        const now = new Date();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30);
        
        const blocksResponse = await api.get('/schedule/work-blocks', {
          params: {
            start: startDate.toISOString(),
            end: endDate.toISOString()
          }
        });
        
        setWorkBlocks(blocksResponse.data || []);
        setShowEventModal(false);
        
        // Show success message
        alert(`Created ${response.data.blocks?.length || 0} work blocks for this task`);
      } else {
        alert(response.data.error || 'Failed to generate work blocks');
      }
    } catch (error) {
      console.error('Failed to generate blocks:', error);
      alert(error.response?.data?.detail || 'Failed to generate work blocks. Please try again.');
    } finally {
      setGeneratingBlocks(false);
    }
  };

  // Toggle handler for completing/uncompleting blocks from calendar event
  const handleToggleBlockComplete = useCallback(async (blockId, isCurrentlyCompleted) => {
    if (isCurrentlyCompleted) {
      await handleUncompleteBlock(blockId);
    } else {
      await handleCompleteBlock(blockId);
    }
  }, []);

  // Create event component with props bound - memoized to prevent recreation
  const EventComponent = useCallback((props) => (
    <CalendarEvent 
      {...props} 
      isTaskOverdue={isTaskOverdue} 
      onToggleComplete={handleToggleBlockComplete}
      completingBlockId={completingBlock}
    />
  ), [completingBlock, handleToggleBlockComplete]);

  // Memoized calendar components object
  const calendarComponents = useMemo(() => ({
    event: EventComponent
  }), [EventComponent]);

  if (loading) {
    return (
      <div className="flex-1 p-4 sm:p-6 w-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="text-center mt-4 text-gray-600">Loading schedule...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 p-4 sm:p-6 w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Smart Schedule</h1>
            <p className="text-gray-600">
              {scheduleView === 'my' 
                ? `Auto-scheduling powered by AI • ${events.filter(e => e.type === 'work-block').length} work blocks`
                : scheduleView === 'team-day'
                ? `Team overview for ${format(teamViewDate, 'EEEE, MMMM d, yyyy')}`
                : teamViewUser 
                ? `${teamViewUser.name}'s schedule • ${getTeamPeriodLabel()}`
                : 'Select a team member'
              }
            </p>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* View Mode Toggle - only for leadership roles */}
            {canViewTeamSchedules && (
              <div className="flex bg-gray-100 rounded-lg p-1" data-testid="schedule-view-toggle">
                <button
                  onClick={() => setScheduleView('my')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    scheduleView === 'my' 
                      ? 'bg-white text-purple-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="my-schedule-btn"
                >
                  <Calendar className="w-4 h-4 inline mr-2" />
                  My Schedule
                </button>
                <button
                  onClick={() => setScheduleView('team-day')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    scheduleView === 'team-day' 
                      ? 'bg-white text-purple-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="team-day-btn"
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Team Day
                </button>
                <button
                  onClick={() => setScheduleView('team-user')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    scheduleView === 'team-user' 
                      ? 'bg-white text-purple-600 shadow-sm' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  data-testid="team-user-btn"
                >
                  <UserCheck className="w-4 h-4 inline mr-2" />
                  Team Member
                </button>
              </div>
            )}

            {/* Show personal schedule controls only in 'my' view */}
            {scheduleView === 'my' && (
              <>
                {/* Quick Add Task button */}
                <button
                  onClick={() => setShowQuickAddTask(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md"
                >
                  <Plus className="w-4 h-4" />
                  <span>Quick Add Task</span>
                </button>

                {/* Reschedule button */}
                <button
                  onClick={rescheduleAll}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Reschedule All</span>
                </button>

                {/* Calendar color settings gear icon */}
                <button
                  onClick={() => setShowColorSettings(true)}
                  className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-600 rounded-lg hover:from-gray-200 hover:to-gray-300 transition-all shadow-md"
                  title="Calendar Color Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>

                {/* User selector for admins */}
                {isAdmin && (
                  <div className="relative">
                    <select
                      value={selectedUser}
                      onChange={(e) => handleUserChange(e.target.value)}
                      className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="me">My Schedule</option>
                      {teamMembers.map(member => (
                        <option key={member.user.id} value={member.user.id}>
                          {member.user.name}&apos;s Schedule
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                )}
              </>
            )}

            {/* Team Day View Controls */}
            {scheduleView === 'team-day' && (
              <div className="flex items-center gap-3">
                {/* Teams Filter Dropdown - only show when feature is enabled */}
                {subTeamsEnabled && subTeams.length > 0 && (
                  <select
                    value={selectedSubTeam || ''}
                    onChange={(e) => setSelectedSubTeam(e.target.value || null)}
                    className="px-3 py-2 border rounded-lg bg-white text-sm min-w-[140px]"
                    data-testid="team-filter-selector"
                  >
                    <option value="">All Members</option>
                    {subTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                )}
                
                <button onClick={goToPreviousDay} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="prev-day-btn">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm font-medium min-w-[180px] text-center">
                  {format(teamViewDate, 'EEEE, MMMM d, yyyy')}
                </span>
                <button onClick={goToNextDay} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="next-day-btn">
                  <ChevronRight className="w-5 h-5" />
                </button>
                <button onClick={goToToday} className="px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-lg" data-testid="today-btn">
                  Today
                </button>
              </div>
            )}

            {/* Team User View Controls */}
            {scheduleView === 'team-user' && (
              <div className="flex items-center gap-3 flex-wrap">
                {/* Teams Filter Dropdown - only show when feature is enabled */}
                {subTeamsEnabled && subTeams.length > 0 && (
                  <select
                    value={selectedSubTeam || ''}
                    onChange={(e) => setSelectedSubTeam(e.target.value || null)}
                    className="px-3 py-2 border rounded-lg bg-white text-sm min-w-[140px]"
                    data-testid="team-filter-selector-user-view"
                  >
                    <option value="">All Members</option>
                    {subTeams.map(team => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                )}
                
                <select
                  value={teamViewTimeframe}
                  onChange={(e) => setTeamViewTimeframe(e.target.value)}
                  className="px-3 py-2 border rounded-lg bg-white text-sm"
                  data-testid="timeframe-selector"
                >
                  <option value="week">Week</option>
                  <option value="2week">2 Weeks</option>
                  <option value="month">Month</option>
                </select>
                
                <div className="flex items-center gap-2">
                  <button onClick={goToPreviousPeriod} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="prev-period-btn">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm font-medium min-w-[140px] text-center">{getTeamPeriodLabel()}</span>
                  <button onClick={goToNextPeriod} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="next-period-btn">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <button onClick={goToToday} className="px-3 py-1 text-sm text-purple-600 hover:bg-purple-50 rounded-lg" data-testid="this-period-btn">
                  {teamViewTimeframe === 'month' ? 'This Month' : teamViewTimeframe === '2week' ? 'Current' : 'This Week'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Conditional based on view */}
      {scheduleView === 'my' ? (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Open Tasks */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <CheckSquare className="h-8 w-8 text-blue-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Open Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {tasks.length}
                  </p>
                </div>
              </div>
            </div>

            {/* Meetings */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <Video className="h-8 w-8 text-green-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Meetings (30 days)</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {meetings.filter(m => {
                      const meetingDate = new Date(m.start_time || m.start);
                      const now = new Date();
                      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                      return meetingDate >= now && meetingDate <= thirtyDaysFromNow;
                    }).length}
                  </p>
                </div>
              </div>
            </div>

            {/* Overdue Tasks */}
            <div 
              className={`bg-white rounded-lg border border-gray-200 p-4 ${
                tasks.filter(t => isTaskOverdue(t)).length > 0 
                  ? 'cursor-pointer hover:shadow-lg transition-shadow border-red-200 bg-red-50' 
                  : ''
              }`}
              onClick={() => {
                if (tasks.filter(t => isTaskOverdue(t)).length > 0) {
                  document.getElementById('overdue-tasks-section')?.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'start' 
                  });
                }
              }}
              title={tasks.filter(t => isTaskOverdue(t)).length > 0 ? 'Click to view overdue tasks' : ''}
            >
              <div className="flex items-center">
                <AlertTriangle className={`h-8 w-8 ${
                  tasks.filter(t => isTaskOverdue(t)).length > 0 
                    ? 'text-red-600' 
                    : 'text-orange-600'
                }`} />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Overdue Tasks</p>
                  <p className={`text-2xl font-bold ${
                    tasks.filter(t => isTaskOverdue(t)).length > 0 
                      ? 'text-red-900' 
                      : 'text-gray-900'
                  }`}>
                    {tasks.filter(t => isTaskOverdue(t)).length}
                  </p>
                </div>
              </div>
            </div>

            {/* Work Hours */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center">
                <Clock className="h-8 w-8 text-indigo-600" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-500">Work Hours</p>
                  <p className="text-2xl font-bold text-gray-900">{workHours.start}-{workHours.end}</p>
                </div>
              </div>
            </div>
          </div>

      {/* Schedule Gap Indicator */}
      {hasScheduleGaps && scheduleView === 'my' && (
        <div className="mb-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="font-medium text-amber-800">Open slots available in your schedule</p>
              <p className="text-sm text-amber-600">
                {isOptimizing ? 'Optimizing...' : 'Click to fill gaps with upcoming tasks'}
              </p>
            </div>
          </div>
          <button
            onClick={handleOptimizeSchedule}
            disabled={isOptimizing}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              isOptimizing 
                ? 'bg-amber-100 text-amber-400 cursor-not-allowed' 
                : 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm hover:shadow'
            }`}
          >
            {isOptimizing ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Optimizing...
              </span>
            ) : (
              'Optimize Schedule'
            )}
          </button>
        </div>
      )}

      {/* Calendar */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="h-[600px]">
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            defaultView={currentView}
            view={currentView}
            onView={setCurrentView}
            date={selectedDate}
            onNavigate={setSelectedDate}
            onSelectEvent={handleEventClick}
            eventPropGetter={eventStyleGetter}
            step={30}
            showMultiDayTimes
            dayLayoutAlgorithm="no-overlap"
            popup
            popupOffset={30}
            scrollToTime={new Date(1970, 1, 1, 8, 0, 0)}
            components={calendarComponents}
            formats={{
              timeGutterFormat: (date, culture, localizer) =>
                localizer.format(date, 'h:mm a', culture),
              eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                `${localizer.format(start, 'h:mm a', culture)} - ${localizer.format(end, 'h:mm a', culture)}`
            }}
          />
        </div>
      </div>

      {/* Overdue Tasks Section - Below Calendar */}
      {tasks.filter(t => isTaskOverdue(t)).length > 0 && (
        <div id="overdue-tasks-section" className="bg-red-50 border-2 border-red-200 rounded-lg p-6 mt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-semibold text-red-900">
                Overdue Tasks ({tasks.filter(t => isTaskOverdue(t)).length})
              </h3>
            </div>
          </div>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto pb-4">
            {tasks
              .filter(t => isTaskOverdue(t))
              .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
              .map(task => {
                const daysOverdue = Math.floor((new Date() - new Date(task.due_date)) / (1000 * 60 * 60 * 24));
                return (
                  <div 
                    key={task.id} 
                    className="bg-white border border-red-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => {
                      // Create an event-like object to open the task modal
                      setSelectedEvent({
                        title: task.name,
                        type: 'task',
                        resource: task,
                        start: new Date(task.due_date),
                        end: new Date(task.due_date)
                      });
                      setShowEventModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <CheckSquare className="w-4 h-4 text-red-600" />
                          <h4 className="font-semibold text-gray-900">{task.name}</h4>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          {task.project_name && (
                            <span className="flex items-center space-x-1 text-gray-600">
                              <Folder className="w-3 h-3" />
                              <span>{task.project_name}</span>
                            </span>
                          )}
                          
                          {task.stage_name && (
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                              {task.stage_name}
                            </span>
                          )}
                          
                          {task.priority && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              task.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                              task.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                              task.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.priority}
                            </span>
                          )}
                          
                          <span className="flex items-center space-x-1 text-red-600 font-semibold">
                            <Calendar className="w-3 h-3" />
                            <span>Due {format(new Date(task.due_date), 'MMM d, yyyy')}</span>
                            <span className="text-red-700">({daysOverdue} {daysOverdue === 1 ? 'day' : 'days'} overdue)</span>
                          </span>
                        </div>
                      </div>
                      
                      {task.percent_complete > 0 && (
                        <div className="ml-4">
                          <div className="text-xs text-gray-600 mb-1">{task.percent_complete}%</div>
                          <div className="w-20 h-2 bg-gray-200 rounded-full">
                            <div 
                              className="h-full bg-red-500 rounded-full" 
                              style={{ width: `${task.percent_complete}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            }
          </div>
        </div>
      )}
        </>
      ) : scheduleView === 'team-day' ? (
        /* ============ Team Day View - Column Layout per Team Member ============ */
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {teamViewLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : teamDaySchedule?.team_members?.length > 0 ? (
            <>
              {/* Summary Stats */}
              <div className="px-6 py-4 bg-gray-50 border-b flex items-center justify-between">
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <span className="font-medium">{teamDaySchedule.summary?.total_users || 0} team members</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span>{teamDaySchedule.summary?.total_work_blocks || 0} work blocks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-green-600" />
                    <span>{teamDaySchedule.summary?.total_meetings || 0} meetings</span>
                  </div>
                </div>
              </div>
              
              {/* Team Members Columns */}
              <div className="grid divide-x overflow-x-auto" style={{ gridTemplateColumns: `repeat(${teamDaySchedule.team_members.length}, minmax(280px, 1fr))` }}>
                {teamDaySchedule.team_members.map((member) => {
                  const avatarUrl = member.user?.avatar_url || member.user?.profile_image_url;
                  const workBlocks = member.work_blocks || [];
                  const meetings = member.meetings || [];
                  
                  return (
                    <div key={member.user.id} className="min-h-[600px] flex flex-col" data-testid={`team-member-column-${member.user.id}`}>
                      {/* Member Header */}
                      <div className="p-4 bg-gray-50 border-b sticky top-0 z-10">
                        <div className="flex items-center gap-3">
                          {avatarUrl ? (
                            <img 
                              src={avatarUrl} 
                              alt={member.user.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-purple-600 font-bold">
                                {member.user.name?.charAt(0)?.toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div>
                            <div className="font-semibold text-gray-900">{member.user.name}</div>
                            <div className="text-xs text-gray-500">
                              {workBlocks.length} blocks • {meetings.length} meetings
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Time-based Schedule */}
                      <div className="flex-1 overflow-y-auto">
                        {(workBlocks.length === 0 && meetings.length === 0) ? (
                          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                            No scheduled items
                          </div>
                        ) : (
                          <div className="p-3 space-y-2">
                            {/* Combine and sort blocks and meetings by time */}
                            {[
                              ...workBlocks.map(b => ({
                                ...b,
                                type: 'block',
                                sortTime: new Date(b.scheduled_start).getTime()
                              })),
                              ...meetings.map(m => ({
                                ...m,
                                type: 'meeting',
                                sortTime: new Date(m.start_time).getTime()
                              }))
                            ].sort((a, b) => a.sortTime - b.sortTime).map(item => (
                              item.type === 'block' ? (
                                <div
                                  key={`block-${item.id}`}
                                  className={`p-3 rounded-lg text-sm border-l-4 ${
                                    item.priority === 'Critical' ? 'bg-red-50 border-red-500' :
                                    item.priority === 'High' ? 'bg-orange-50 border-orange-500' :
                                    item.priority === 'Low' ? 'bg-gray-50 border-gray-400' :
                                    'bg-blue-50 border-blue-500'
                                  }`}
                                >
                                  <div className="font-medium text-gray-900 truncate">{item.task_name}</div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {formatTeamTime(item.scheduled_start)} - {formatTeamTime(item.scheduled_end)}
                                  </div>
                                  {item.project_name && (
                                    <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                      <Folder className="w-3 h-3" />
                                      {item.project_name}
                                    </div>
                                  )}
                                  {item.total_blocks > 1 && (
                                    <div className="text-xs text-gray-400 mt-1">
                                      Part {item.block_number}/{item.total_blocks}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div
                                  key={`meeting-${item.id}`}
                                  className="p-3 rounded-lg text-sm bg-green-50 border-l-4 border-green-500"
                                >
                                  <div className="font-medium text-gray-900 truncate flex items-center gap-2">
                                    <Video className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    {item.title}
                                  </div>
                                  <div className="text-xs text-gray-600 mt-1">
                                    {formatTeamTime(item.start_time)} - {formatTeamTime(item.end_time)}
                                  </div>
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              No team schedules found for this day
            </div>
          )}
        </div>
      ) : scheduleView === 'team-user' ? (
        /* ============ Team User View - Using BigCalendar ============ */
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {teamViewLoading && !displayTeamMembers.length ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : (
            <>
              {/* Team Member Selector - Prominent Card-Based Selection */}
              <div className="p-6 border-b bg-gradient-to-r from-slate-50 to-gray-50">
                <h3 className="text-sm font-medium text-gray-500 mb-4">
                  Select Team Member
                  {subTeamsEnabled && selectedSubTeam && subTeams.find(t => t.id === selectedSubTeam) && (
                    <span className="ml-2 text-purple-600">
                      • {subTeams.find(t => t.id === selectedSubTeam)?.name}
                    </span>
                  )}
                </h3>
                <div className="flex flex-wrap gap-3">
                  {displayTeamMembers.map(member => {
                    const memberUser = member.user || member;
                    const isSelected = teamViewUser?.id === memberUser.id;
                    const avatarUrl = memberUser.avatar_url || memberUser.profile_image_url;
                    
                    return (
                      <button
                        key={memberUser.id}
                        onClick={() => setTeamViewUser(memberUser)}
                        data-testid={`team-member-card-${memberUser.id}`}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isSelected 
                            ? 'bg-purple-600 text-white shadow-lg scale-105' 
                            : 'bg-white border border-gray-200 hover:border-purple-300 hover:shadow-md'
                        }`}
                      >
                        {avatarUrl ? (
                          <img 
                            src={avatarUrl} 
                            alt={memberUser.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-purple-500' : 'bg-purple-100'
                          }`}>
                            <span className={`font-bold text-lg ${isSelected ? 'text-white' : 'text-purple-600'}`}>
                              {memberUser.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="text-left">
                          <div className={`font-semibold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                            {memberUser.name}
                          </div>
                          <div className={`text-xs ${isSelected ? 'text-purple-200' : 'text-gray-500'}`}>
                            {memberUser.role || 'Team Member'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Selected User Stats */}
              {teamUserSchedule && (
                <div className="px-6 py-4 bg-white border-b flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {(teamUserSchedule.user?.avatar_url || teamUserSchedule.user?.profile_image_url) ? (
                      <img 
                        src={teamUserSchedule.user.avatar_url || teamUserSchedule.user.profile_image_url} 
                        alt={teamUserSchedule.user.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-purple-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center border-2 border-purple-200">
                        <span className="text-purple-600 font-bold text-xl">
                          {teamUserSchedule.user?.name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{teamUserSchedule.user?.name}</h2>
                      <p className="text-sm text-gray-500">
                        {teamUserSchedule.user?.role} • {teamUserSchedule.user?.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Period Stats */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-purple-600">{teamUserSchedule.work_blocks?.length || 0}</div>
                      <div className="text-xs text-gray-500">Work Blocks</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">{teamUserSchedule.meetings?.length || 0}</div>
                      <div className="text-xs text-gray-500">Meetings</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-600">
                        {Math.round((teamUserSchedule.work_blocks || []).reduce((acc, block) => {
                          if (block.scheduled_start && block.scheduled_end) {
                            return acc + (new Date(block.scheduled_end) - new Date(block.scheduled_start)) / (1000 * 60);
                          }
                          return acc;
                        }, 0) / 60 * 10) / 10}h
                      </div>
                      <div className="text-xs text-gray-500">Scheduled</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading state for calendar */}
              {teamViewLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
              ) : teamUserSchedule ? (
                /* BigCalendar */
                <div className="p-6">
                  <div className="h-[550px]">
                    <BigCalendar
                      localizer={localizer}
                      events={teamUserEvents}
                      startAccessor="start"
                      endAccessor="end"
                      defaultView={teamViewTimeframe === 'month' ? 'month' : 'week'}
                      view={teamViewTimeframe === 'month' ? 'month' : 'week'}
                      views={['week', 'month']}
                      date={teamViewDate}
                      onNavigate={setTeamViewDate}
                      onView={(view) => setTeamViewTimeframe(view === 'month' ? 'month' : 'week')}
                      toolbar={false}
                      eventPropGetter={teamEventStyleGetter}
                      step={30}
                      showMultiDayTimes
                      dayLayoutAlgorithm="no-overlap"
                      popup
                      popupOffset={30}
                      scrollToTime={new Date(1970, 1, 1, 8, 0, 0)}
                      formats={{
                        timeGutterFormat: (date, culture, localizer) =>
                          localizer.format(date, 'h:mm a', culture),
                        eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                          `${localizer.format(start, 'h:mm a', culture)} - ${localizer.format(end, 'h:mm a', culture)}`
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  Select a team member to view their schedule
                </div>
              )}
            </>
          )}
        </div>
      ) : null}

      {/* Quick Add Task Modal */}
      <QuickAddTaskModal
        isOpen={showQuickAddTask}
        onClose={() => setShowQuickAddTask(false)}
        onTaskCreated={handleTaskCreated}
        onOpenFullEditor={handleOpenFullEditor}
        user={user}
      />

      {/* Calendar Color Settings Modal */}
      {showColorSettings && (
        <CalendarColorSettingsModal 
          isOpen={showColorSettings}
          onClose={() => setShowColorSettings(false)}
          currentColors={userColorSettings}
          onColorsUpdate={setUserColorSettings}
        />
      )}

      {/* Event Details Modal */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {selectedEvent.type === 'work-block' ? (
                    <CheckSquare className="w-6 h-6 text-purple-600" />
                  ) : selectedEvent.type === 'task' ? (
                    <CheckSquare className="w-6 h-6 text-purple-600" />
                  ) : (
                    <Calendar className="w-6 h-6 text-green-600" />
                  )}
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedEvent.title}</h2>
                    <p className="text-sm text-gray-600">
                      {selectedEvent.type === 'work-block' 
                        ? `Work Block ${selectedEvent.resource?.block_number || selectedEvent.resource?.segmentNumber} of ${selectedEvent.resource?.total_blocks || selectedEvent.resource?.totalSegments}` 
                        : selectedEvent.type === 'task' 
                          ? 'Task Details' 
                          : 'Meeting Details'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Privacy notice for masked tasks */}
              {selectedEvent.resource?.privacy_masked && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2">
                    <Lock className="w-5 h-5 text-gray-500" />
                    <p className="text-sm text-gray-700 font-medium">Private Personal Task</p>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    This is a personal task belonging to another user. Details are kept private.
                  </p>
                </div>
              )}

              {/* Time Information */}
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {format(selectedEvent.start, 'MMM d, yyyy')}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    {format(selectedEvent.start, 'h:mm a')} - {format(selectedEvent.end, 'h:mm a')}
                  </span>
                </div>
              </div>

              {/* Task/Meeting/Work Block Details */}
              {!selectedEvent.resource?.privacy_masked && (
                <>
                  {selectedEvent.resource?.description && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                      <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
                        {selectedEvent.resource.description}
                      </p>
                    </div>
                  )}

                  {/* Work Block Progress */}
                  {selectedEvent.type === 'work-block' && (
                    <>
                      {/* Progress Bar */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">Task Progress</span>
                          <span className="text-sm font-medium text-purple-600">
                            {selectedEvent.resource?.task_percent_complete || 0}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                            style={{ width: `${selectedEvent.resource?.task_percent_complete || 0}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-2 text-xs text-gray-500">
                          <span>Block {selectedEvent.resource?.block_number || selectedEvent.resource?.segmentNumber} of {selectedEvent.resource?.total_blocks || selectedEvent.resource?.totalSegments}</span>
                          <span>{selectedEvent.resource?.duration_minutes || Math.round((selectedEvent.end - selectedEvent.start) / 60000)} minutes</span>
                        </div>
                      </div>

                      {selectedEvent.resource?.project_name && (
                        <div className="flex items-center space-x-3">
                          <Folder className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Project:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedEvent.resource.project_name}
                          </span>
                        </div>
                      )}

                      {selectedEvent.resource?.priority && (
                        <div className="flex items-center space-x-3">
                          <Flag className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Priority:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedEvent.resource.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                            selectedEvent.resource.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                            selectedEvent.resource.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedEvent.resource.priority}
                          </span>
                        </div>
                      )}

                      {selectedEvent.resource?.status === 'completed' && (
                        <div className="flex items-center space-x-3">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-sm text-green-600 font-medium">
                            Completed {selectedEvent.resource.completed_at && `on ${format(new Date(selectedEvent.resource.completed_at), 'MMM d, yyyy h:mm a')}`}
                          </span>
                        </div>
                      )}

                      {/* Due Date for Work Blocks */}
                      {selectedEvent.resource?.due_date && (
                        <div className="flex items-center space-x-3">
                          <AlertTriangle className={`w-4 h-4 ${
                            new Date(selectedEvent.resource.due_date) < new Date() ? 'text-red-500' : 'text-gray-400'
                          }`} />
                          <span className="text-sm text-gray-600">Due:</span>
                          <span className={`text-sm font-medium ${
                            new Date(selectedEvent.resource.due_date) < new Date() ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {format(new Date(selectedEvent.resource.due_date), 'MMM d, yyyy')}
                            {new Date(selectedEvent.resource.due_date) < new Date() && ' (Overdue)'}
                          </span>
                        </div>
                      )}

                      {/* View Full Task Button for Work Blocks */}
                      <button
                        onClick={() => {
                          setShowEventModal(false);
                          const taskId = selectedEvent.resource?.task_id;
                          if (taskId) {
                            // Store return path in sessionStorage for reliable navigation back
                            sessionStorage.setItem('taskModalReturnPath', '/schedule');
                            navigate(`/tasks/${taskId}`);
                          }
                        }}
                        className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                        data-testid="view-task-button"
                      >
                        <span>View Full Task Details</span>
                      </button>
                    </>
                  )}

                  {selectedEvent.type === 'task' && (
                    <>
                      {selectedEvent.resource?.priority && (
                        <div className="flex items-center space-x-3">
                          <Flag className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Priority:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedEvent.resource.priority === 'Critical' ? 'bg-red-100 text-red-800' :
                            selectedEvent.resource.priority === 'High' ? 'bg-orange-100 text-orange-800' :
                            selectedEvent.resource.priority === 'Normal' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedEvent.resource.priority}
                          </span>
                        </div>
                      )}

                      {/* Due Date */}
                      {selectedEvent.resource?.due_date && (
                        <div className="flex items-center space-x-3">
                          <AlertTriangle className={`w-4 h-4 ${
                            new Date(selectedEvent.resource.due_date) < new Date() ? 'text-red-500' : 'text-gray-400'
                          }`} />
                          <span className="text-sm text-gray-600">Due:</span>
                          <span className={`text-sm font-medium ${
                            new Date(selectedEvent.resource.due_date) < new Date() ? 'text-red-600' : 'text-gray-900'
                          }`}>
                            {format(new Date(selectedEvent.resource.due_date), 'MMM d, yyyy')}
                            {new Date(selectedEvent.resource.due_date) < new Date() && ' (Overdue)'}
                          </span>
                        </div>
                      )}

                      {selectedEvent.resource?.status && (
                        <div className="flex items-center space-x-3">
                          <CheckSquare className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Status:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedEvent.resource.status === 'Done' ? 'bg-green-100 text-green-800' :
                            selectedEvent.resource.status === 'In Progress' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {selectedEvent.resource.status}
                          </span>
                        </div>
                      )}

                      {selectedEvent.resource?.estimated_effort && (
                        <div className="flex items-center space-x-3">
                          <Clock className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">Estimated Effort:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {selectedEvent.resource.estimated_effort}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <div>
                  {/* Hide from Schedule button - only for external events (meetings), not tasks */}
                  {selectedEvent.type !== 'task' && selectedEvent.type !== 'work-block' && selectedEvent.resource?.id && (
                    <button
                      onClick={handleHideEvent}
                      className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors flex items-center space-x-2"
                    >
                      <X className="w-4 h-4" />
                      <span>Hide from Schedule</span>
                    </button>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setShowEventModal(false)}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  
                  {/* Work Block Actions */}
                  {selectedEvent.type === 'work-block' && selectedEvent.resource?.blockId && (
                    <>
                      {selectedEvent.resource?.status === 'completed' ? (
                        <button
                          onClick={() => handleUncompleteBlock(selectedEvent.resource.blockId)}
                          disabled={completingBlock === selectedEvent.resource.blockId}
                          className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
                        >
                          <RotateCcw className="w-4 h-4" />
                          <span>{completingBlock === selectedEvent.resource.blockId ? 'Undoing...' : 'Undo Complete'}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handleCompleteBlock(selectedEvent.resource.blockId)}
                          disabled={completingBlock === selectedEvent.resource.blockId}
                          className="px-4 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all flex items-center space-x-2 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" />
                          <span>{completingBlock === selectedEvent.resource.blockId ? 'Completing...' : 'Mark Complete'}</span>
                        </button>
                      )}
                    </>
                  )}
                  
                  {!selectedEvent.resource?.privacy_masked && selectedEvent.type === 'task' && (
                    <>
                      {/* Generate Work Blocks button */}
                      <button
                        onClick={() => handleGenerateBlocks(selectedEvent.resource.id)}
                        disabled={generatingBlocks}
                        className="px-4 py-2 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
                      >
                        <Calendar className="w-4 h-4" />
                        <span>{generatingBlocks ? 'Scheduling...' : 'Schedule Work Blocks'}</span>
                      </button>
                      
                      <button
                        onClick={handleEditTask}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all"
                      >
                        Edit Task
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Editor Modal */}
    </div>
  );
};

export default ScheduleDashboard;